/*
  VAULT — lookbook personalization (idol, group and product pages).
  Deterministic, explainable re-ordering of ELIGIBLE Looks. Never adds content.

  Pipeline: eligible → hidden filter → canonical IDs → pins → editorial baseline → long-term affinity
            → session intent → page confidence → blended score → page diversity → tie-break → render once.

  One file, two uses:
   - browser: window.VaultRank (pure engine) + window.VaultPZ (local store + event tracking)
   - tests:   require('assets/vault-personalize.js') → the same pure engine (tests/rank.test.mjs)

  Storage lives only in this browser (localStorage): up to 180 days of history that is still available.
  Starts on every page for every visitor (no separate consent switch). Without localStorage: editorial order.
  Debug: ?vault-pz-debug=1 (full report in the console + window.__vaultPzDebug).
  Isolation test: ?vault-pz-force=1 → confidence forced to 1 (pure personalization), never for shoppers.
  Look IDs are look:<source page.id>:<block.id>. block.id can change if a block is recreated; that only
  resets that Look's seen history, never artist/group/product/brand/category affinity.

  FUTURE — REQUEST THIS ITEM: call VaultPZ.track('request_item', { product, artist, groups, fromLook })
  from the future request button. Weight (0.90) and credits are already configured (creditsFor()).
*/
(function (root, factory) {
  var engine = factory();
  if (typeof module === 'object' && module.exports) module.exports = engine;
  else root.VaultRank = engine;
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  /* ================= CENTRAL CONFIGURATION ================= */
  var CONFIG = {
    EVENT_WEIGHTS: {
      qualified_page_view: 0.10, // documented only: not stored or used in V1
      group_page_view: 0.15,
      idol_page_view: 0.20,
      product_page_view: 0.25,
      look_swipe: 0.20,
      lookbook_open: 0.30,
      hotspot_open: 0.50,
      product_click_from_look: 0.70,
      repeated_product_view: 0.75,
      add_to_cart: 0.95,
      purchase: 1.00,            // supported, never emitted in V1 (not observable by the theme)
      request_item: 0.90         // future
    },
    PROPAGATION: {
      artist: 1.0, artistToGroup: 0.25,
      product: 1.0, brand: 0.50, category: 0.35,
      categoryLeafShare: 0.7, categoryParentShare: 0.3,
      lookArtistFromProductEvent: 0.5
    },
    AFFINITY_HALF_LIFE_DAYS: 30,
    AFFINITY_MAX_AGE_DAYS: 180,
    AFFINITY_SATURATION: 1.5,   // raw 1.5 (≈ 4 meaningful interactions) → affinity 0.63
    SESSION_TIMEOUT_MINUTES: 30,
    SESSION_SATURATION: 1,
    FRESHNESS_DECAY_DAYS: 5,
    SEEN_HISTORY_DAYS: 180,
    CONFIDENCE_SATURATION: 1.5, // confidence = min(CAP, 1 − e^(−evidence / 1.5)); evidence = decayed event weights that tell this page's candidates apart
    CONFIDENCE_CAP: 0.9,
    MAX_EVENTS: 2000,
    MAX_SEEN_LOOKS: 1000,
    QUALIFIED_PAGE_VIEW_MS: 2000,
    QUALIFIED_IMPRESSION_MS: 1000,
    DIVERSITY_NEAR_SCORE_RATIO: 0.85,
    DIVERSITY_TOP_N: 5,
    BASELINE_WEIGHTS: { position: 0.75, freshness: 0.15, priority: 0.10 },
    BASELINE_POSITION_SPAN: 8,  // position = 1 − index / max(n − 1, 8): neighbours are ≤ 1/8 apart however short the list
    PRODUCT_RELEVANCE_WEIGHTS: { exact: 0.60, brand: 0.25, category: 0.15 },
    EXACT_MATCH_WEIGHTS: { best: 0.75, second: 0.25 },
    SESSION_GROUP_PAGE_WEIGHTS: { artist: 0.60, product: 0.40 },
    SESSION_PRODUCT_PAGE_WEIGHTS: { artist: 0.70, group: 0.30 },
    PAGE_SCORE_WEIGHTS: {
      idol:    { product: 0.50, sessionProduct: 0.25, freshness: 0.10, unseen: 0.08, priority: 0.07 },
      group:   { artist: 0.40, product: 0.22, sessionGroup: 0.23, freshness: 0.06, unseen: 0.05, priority: 0.04 },
      product: { artist: 0.45, group: 0.10, sessionProductPage: 0.30, freshness: 0.06, unseen: 0.05, priority: 0.04 }
    }
  };

  var DAY = 86400000;
  function clamp01(x) { return x < 0 ? 0 : x > 1 ? 1 : (x || 0); }
  function saturate(raw, s) { return clamp01(1 - Math.exp(-(raw || 0) / s)); }
  function decay(ageDays) {
    if (ageDays < 0) ageDays = 0;
    if (ageDays > CONFIG.AFFINITY_MAX_AGE_DAYS) return 0;
    return Math.exp(-Math.LN2 * ageDays / CONFIG.AFFINITY_HALF_LIFE_DAYS);
  }

  /* ---------- dates: strict YYYY-MM-DD, UTC calendar days ---------- */
  function parseVaultDate(str) {
    if (typeof str !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(str)) return null;
    var y = +str.slice(0, 4), m = +str.slice(5, 7), d = +str.slice(8, 10);
    var t = Date.UTC(y, m - 1, d), dt = new Date(t);
    if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== m - 1 || dt.getUTCDate() !== d) return null;
    return t;
  }
  function utcDay(now) { var d = new Date(now); return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()); }
  function freshnessScore(addedStr, now) {
    var t = parseVaultDate(addedStr);
    if (t === null) return 0;
    var age = Math.floor((utcDay(now) - t) / DAY);
    if (age < 0) age = 0; // future date → age 0
    return clamp01(Math.exp(-age / CONFIG.FRESHNESS_DECAY_DAYS));
  }

  /* ---------- event → entity credit matrix (propagation happens once, here) ----------
     ctx: { page, artist, groups[], pageGroup, product: {id, brands[], cats[{id,w}]}, fromLook } */
  function productCredits(p, out) {
    if (!p || !p.id) return;
    out.push([p.id, CONFIG.PROPAGATION.product]);
    var b = p.brands || [];
    b.forEach(function (id) { out.push([id, CONFIG.PROPAGATION.brand / b.length]); });
    (p.cats || []).forEach(function (c) { out.push([c.id, CONFIG.PROPAGATION.category * c.w]); });
  }
  function artistCredits(artist, groups, factor, out) {
    if (!artist) return;
    out.push([artist, CONFIG.PROPAGATION.artist * factor]);
    (groups || []).forEach(function (g) { out.push([g, CONFIG.PROPAGATION.artistToGroup * factor]); });
  }
  function creditsFor(type, ctx) {
    var out = [];
    ctx = ctx || {};
    switch (type) {
      case 'idol_page_view': artistCredits(ctx.artist, ctx.groups, 1, out); break;
      case 'group_page_view': if (ctx.pageGroup) out.push([ctx.pageGroup, 1]); break; // members get nothing
      case 'product_page_view':
      case 'repeated_product_view':
      case 'add_to_cart':
      case 'purchase': productCredits(ctx.product, out); break;
      case 'lookbook_open':
        if (ctx.page === 'idol') artistCredits(ctx.artist, ctx.groups, 1, out);
        else if (ctx.page === 'group') { if (ctx.pageGroup) out.push([ctx.pageGroup, 1]); }
        else if (ctx.page === 'product') productCredits(ctx.product, out);
        break;
      case 'look_swipe': artistCredits(ctx.artist, ctx.groups, 1, out); break;
      case 'hotspot_open':
      case 'product_click_from_look':
      case 'request_item':
        if (ctx.product) productCredits(ctx.product, out);
        if (ctx.artist && (type !== 'request_item' || ctx.fromLook)) artistCredits(ctx.artist, ctx.groups, CONFIG.PROPAGATION.lookArtistFromProductEvent, out);
        break;
      default: break; // qualified_page_view, impressions: no credits
    }
    return out;
  }

  /* ---------- shape checks: stored data is untrusted; anything unexpected is dropped, never repaired ---------- */
  function isObj(x) { return !!x && typeof x === 'object' && !Array.isArray(x); }
  function isNum(x) { return typeof x === 'number' && isFinite(x); }
  function validCredit(cr) { return Array.isArray(cr) && cr.length === 2 && typeof cr[0] === 'string' && isNum(cr[1]); }
  function validEvent(e) {
    return isObj(e) && isNum(e.t) && typeof e.ty === 'string' && Array.isArray(e.c) && e.c.every(validCredit) &&
      (e.s == null || typeof e.s === 'string');
  }

  /* ---------- storage pruning (pure) ---------- */
  function pruneEvents(events, now) {
    var cutoff = now - CONFIG.AFFINITY_MAX_AGE_DAYS * DAY;
    var kept = (Array.isArray(events) ? events : []).filter(function (e) { return validEvent(e) && e.t >= cutoff; });
    kept.sort(function (a, b) { return a.t - b.t; });
    if (kept.length > CONFIG.MAX_EVENTS) kept = kept.slice(kept.length - CONFIG.MAX_EVENTS);
    return kept;
  }
  function pruneSeen(seen, now) {
    var cutoff = now - CONFIG.SEEN_HISTORY_DAYS * DAY;
    if (!isObj(seen)) seen = {};
    var list = Object.keys(seen).map(function (k) { return [k, seen[k]]; }).filter(function (x) { return isNum(x[1]) && x[1] >= cutoff; });
    list.sort(function (a, b) { return b[1] - a[1]; });
    var out = {};
    list.slice(0, CONFIG.MAX_SEEN_LOOKS).forEach(function (x) { out[x[0]] = x[1]; });
    return out;
  }

  /* ---------- affinity: long-term (decayed, saturated) and session (separate accumulator) ---------- */
  function computeProfile(events, now, sessionId) {
    var longRaw = {}, sessRaw = {};
    (Array.isArray(events) ? events : []).forEach(function (e) {
      if (!validEvent(e)) return;
      var w = CONFIG.EVENT_WEIGHTS[e.ty] || 0;
      if (!w) return;
      var d = decay((now - e.t) / DAY);
      var inSession = !!sessionId && e.s === sessionId;
      e.c.forEach(function (cr) {
        if (d > 0) longRaw[cr[0]] = (longRaw[cr[0]] || 0) + w * cr[1] * d;
        if (inSession) sessRaw[cr[0]] = (sessRaw[cr[0]] || 0) + w * cr[1];
      });
    });
    var long = {}, session = {};
    Object.keys(longRaw).forEach(function (k) { long[k] = saturate(longRaw[k], CONFIG.AFFINITY_SATURATION); });
    Object.keys(sessRaw).forEach(function (k) { session[k] = saturate(sessRaw[k], CONFIG.SESSION_SATURATION); });
    return { long: long, session: session };
  }
  function aff(map, id) { return (map && id && map[id]) || 0; }
  function maxAff(map, ids) { var m = 0; (ids || []).forEach(function (id) { m = Math.max(m, aff(map, id)); }); return m; }

  /* ---------- look product relevance: top-two exact, strongest brand, strongest category ---------- */
  function lookProductRelevance(look, map) {
    var prods = look.products || [];
    var scores = prods.map(function (p) { return aff(map, p.id); }).sort(function (a, b) { return b - a; });
    var W = CONFIG.EXACT_MATCH_WEIGHTS, R = CONFIG.PRODUCT_RELEVANCE_WEIGHTS;
    var exact = W.best * (scores[0] || 0) + W.second * (scores[1] || 0);
    var brand = 0, cat = 0;
    prods.forEach(function (p) {
      brand = Math.max(brand, maxAff(map, p.brands));
      (p.cats || []).forEach(function (c) { cat = Math.max(cat, aff(map, c.id)); });
    });
    return clamp01(R.exact * exact + R.brand * brand + R.category * cat);
  }

  /* ---------- confidence: differentiating evidence only, each event counted once ---------- */
  function lookEntities(look, page) {
    var set = {};
    if (page === 'group' || page === 'product') { if (look.artist) set[look.artist] = 1; }
    if (page === 'product') (look.groups || []).forEach(function (g) { set[g] = 1; });
    if (page === 'idol' || page === 'group') {
      (look.products || []).forEach(function (p) {
        set[p.id] = 1;
        (p.brands || []).forEach(function (b) { set[b] = 1; });
        (p.cats || []).forEach(function (c) { set[c.id] = 1; });
      });
    }
    return set;
  }
  function differentiatingEntities(cands, page) {
    var n = cands.length, count = {}, out = {};
    if (n < 2) return out;
    cands.forEach(function (l) { Object.keys(lookEntities(l, page)).forEach(function (k) { count[k] = (count[k] || 0) + 1; }); });
    Object.keys(count).forEach(function (k) { if (count[k] < n) out[k] = 1; }); // shared by all = can't differentiate
    return out;
  }
  function contextConfidence(events, now, cands, page) {
    var diff = differentiatingEntities(cands, page);
    if (!Object.keys(diff).length) return 0;
    var raw = 0;
    (Array.isArray(events) ? events : []).forEach(function (e) {
      if (!validEvent(e)) return;
      var w = CONFIG.EVENT_WEIGHTS[e.ty] || 0;
      if (!w) return;
      if (e.c.some(function (cr) { return diff[cr[0]]; })) raw += w * decay((now - e.t) / DAY);
    });
    return Math.min(CONFIG.CONFIDENCE_CAP, saturate(raw, CONFIG.CONFIDENCE_SATURATION));
  }

  /* ---------- page scores ---------- */
  function personalizedScore(look, page, profile, extras) {
    var L = profile.long, S = profile.session, W = CONFIG.PAGE_SCORE_WEIGHTS[page];
    var parts = { freshness: extras.freshness, unseen: extras.unseen, priority: extras.priority };
    if (page === 'idol') {
      parts.product = lookProductRelevance(look, L);
      parts.sessionProduct = lookProductRelevance(look, S);
    } else if (page === 'group') {
      parts.artist = aff(L, look.artist);
      parts.product = lookProductRelevance(look, L);
      var G = CONFIG.SESSION_GROUP_PAGE_WEIGHTS;
      parts.sessionGroup = clamp01(G.artist * aff(S, look.artist) + G.product * lookProductRelevance(look, S));
    } else {
      parts.artist = aff(L, look.artist);
      parts.group = maxAff(L, look.groups); // strongest group, never summed
      var P = CONFIG.SESSION_PRODUCT_PAGE_WEIGHTS;
      parts.sessionProductPage = clamp01(P.artist * aff(S, look.artist) + P.group * maxAff(S, look.groups));
    }
    var s = 0;
    Object.keys(W).forEach(function (k) { s += W[k] * (parts[k] || 0); });
    return { score: clamp01(s), parts: parts };
  }

  /* ---------- soft diversity (after scoring, never touches pins) ---------- */
  function diversityKeys(page) { return page === 'group' ? ['artist'] : page === 'product' ? ['group', 'artist'] : []; }
  function keyOf(look, k) { return k === 'group' ? ((look.groups || [])[0] || look.artist) : look.artist; }
  function applyDiversity(list, page, log) {
    var keys = diversityKeys(page);
    if (!keys.length) return list;
    var out = list.slice(), N = Math.min(CONFIG.DIVERSITY_TOP_N, out.length);
    for (var i = 1; i < N; i++) {
      var cur = out[i], needs = null;
      for (var ki = 0; ki < keys.length && !needs; ki++) {
        var k = keys[ki], kv = keyOf(cur.look, k), same = 0;
        for (var a = 0; a < i; a++) if (keyOf(out[a].look, k) === kv) same++;
        if (keyOf(out[i - 1].look, k) === kv || same >= 2) needs = k;
      }
      if (!needs) continue;
      var bad = keyOf(cur.look, needs);
      for (var j = i + 1; j < out.length; j++) {
        var alt = out[j];
        if (keyOf(alt.look, needs) === bad) continue;
        if (alt.final >= cur.final * CONFIG.DIVERSITY_NEAR_SCORE_RATIO) {
          out.splice(j, 1); out.splice(i, 0, alt);
          if (log) log.push(alt.look.id + ' (' + alt.final.toFixed(3) + ') moved ahead of ' + cur.look.id + ' (' + cur.final.toFixed(3) + '): different ' + needs + ', within ' + CONFIG.DIVERSITY_NEAR_SCORE_RATIO + ' of its score');
          alt.diversity = 'moved up to ' + (i + 1) + ' (different ' + needs + ')';
          cur.diversity = 'moved down (' + needs + ' repeat)';
        }
        break; // only the best candidate with a different key is considered
      }
    }
    return out;
  }

  /* ---------- rank: the whole pipeline ----------
     input: { page:'idol'|'group'|'product', looks:[...], now, events, sessionId, seen, consent }
     look:  { id, artist, groups[], products[{id,brands[],cats[{id,w}]}], pos, pinned, priority 0-10,
              added 'YYYY-MM-DD', hidden, curatedPin (index in "Show these Looks first", else -1) } */
  function rank(input) {
    var page = input.page, now = input.now || Date.now();
    var all = (input.looks || []).filter(function (l) { return !l.hidden; });
    function byDefault(a, b) { return (a.pos - b.pos) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0); }
    function isCurated(l) { return typeof l.curatedPin === 'number' && l.curatedPin >= 0; }
    var curated = all.filter(isCurated).sort(function (a, b) { return a.curatedPin - b.curatedPin; });
    var pins = all.filter(function (l) { return !isCurated(l) && l.pinned; })
      .sort(function (a, b) { return ((b.priority || 0) - (a.priority || 0)) || byDefault(a, b); });
    var rest = all.filter(function (l) { return !isCurated(l) && !l.pinned; }).sort(byDefault);
    var head = curated.concat(pins);
    var debug = { page: page, consent: !!input.consent, confidence: 0, rows: [], diversity: [] };

    if (!input.consent) { // no consent: exact default order (pins first), no freshness / priority
      var plain = head.concat(rest);
      debug.rows = plain.map(function (l, i) { return { lookId: l.id, pinned: isCurated(l) ? 'show-first' : l.pinned ? 'pin' : '', position: i + 1 }; });
      return { order: plain.map(function (l) { return l.id; }), debug: debug };
    }

    var events = pruneEvents(input.events, now);
    var profile = computeProfile(events, now, input.sessionId);
    debug.profile = profile; debug.eventCount = events.length; debug.sessionId = input.sessionId || null;
    var seen = pruneSeen(input.seen || {}, now);
    var conf = typeof input.forceConfidence === 'number' ? clamp01(input.forceConfidence) : contextConfidence(events, now, rest, page);
    debug.confidence = conf;
    var n = rest.length, B = CONFIG.BASELINE_WEIGHTS;
    var scored = rest.map(function (l, i) {
      var position = n > 1 ? 1 - i / Math.max(n - 1, CONFIG.BASELINE_POSITION_SPAN) : 1;
      var fresh = freshnessScore(l.added, now);
      var prio = clamp01((l.priority || 0) / 10);
      var unseen = seen[l.id] ? 0 : 1;
      var baseline = clamp01(B.position * position + B.freshness * fresh + B.priority * prio);
      var p = personalizedScore(l, page, profile, { freshness: fresh, unseen: unseen, priority: prio });
      var final = clamp01((1 - conf) * baseline + conf * p.score);
      return { look: l, i: i, baseline: baseline, personal: p.score, parts: p.parts, final: final, diversity: '' };
    });
    scored.sort(function (a, b) { return (b.final - a.final) || (a.i - b.i) || (a.look.id < b.look.id ? -1 : 1); });
    scored = applyDiversity(scored, page, debug.diversity);
    debug.rows = head.map(function (l, i) { return { lookId: l.id, label: l.label || '', artistId: l.artist || '', defaultPos: l.pos + 1, pinned: isCurated(l) ? 'show-first' : 'pin', position: i + 1 }; })
      .concat(scored.map(function (s, k) {
        return { lookId: s.look.id, label: s.look.label || '', artistId: s.look.artist || '', defaultPos: s.look.pos + 1, pinned: '', baseline: +s.baseline.toFixed(4), personalized: +s.personal.toFixed(4),
          artist: s.parts.artist, group: s.parts.group, product: s.parts.product,
          session: s.parts.sessionProduct != null ? s.parts.sessionProduct : s.parts.sessionGroup != null ? s.parts.sessionGroup : s.parts.sessionProductPage,
          freshness: s.parts.freshness, unseen: s.parts.unseen, priority: s.parts.priority, confidence: +conf.toFixed(4),
          final: +s.final.toFixed(4), diversity: s.diversity, position: head.length + k + 1 };
      }));
    return { order: head.concat(scored.map(function (s) { return s.look; })).map(function (l) { return l.id; }), debug: debug };
  }

  return {
    CONFIG: CONFIG, clamp01: clamp01, saturate: saturate, decay: decay,
    validEvent: validEvent, parseVaultDate: parseVaultDate, freshnessScore: freshnessScore,
    creditsFor: creditsFor, pruneEvents: pruneEvents, pruneSeen: pruneSeen,
    computeProfile: computeProfile, lookProductRelevance: lookProductRelevance,
    differentiatingEntities: differentiatingEntities, contextConfidence: contextConfidence,
    personalizedScore: personalizedScore, applyDiversity: applyDiversity, rank: rank
  };
});

/* ================= BROWSER: local store, tracking, page readers ================= */
(function () {
  if (typeof window === 'undefined' || !window.VaultRank || window.VaultPZ) return;
  var R = window.VaultRank, C = R.CONFIG;
  var K = { vid: 'vault_pz_vid', sess: 'vault_pz_session', ev: 'vault_pz_events', seen: 'vault_pz_seen', rep: 'vault_pz_repeat', dd: 'vault_pz_dedupe' };
  var mem = null; // in-memory state (rebuilt from localStorage on every page load)

  /* Personalization runs for every visitor from the first page (no separate consent switch, by decision
     2026-10-01). It only needs working localStorage; without it the editorial order is used. */
  var storageOk = null;
  function allowed() {
    if (storageOk !== null) return storageOk;
    try { var k = 'vault_pz_probe'; window.localStorage.setItem(k, '1'); window.localStorage.removeItem(k); storageOk = true; }
    catch (e) { storageOk = false; }
    return storageOk;
  }
  function isPlain(x) { return !!x && typeof x === 'object' && !Array.isArray(x); }
  function lsGet(k) { try { return JSON.parse(window.localStorage.getItem(k) || 'null'); } catch (e) { return null; } }
  function lsSet(k, v) { try { window.localStorage.setItem(k, JSON.stringify(v)); } catch (e) {} }
  function wipe() {
    mem = null;
    Object.keys(K).forEach(function (k) { try { window.localStorage.removeItem(K[k]); } catch (e) {} });
    try { window.sessionStorage.removeItem('vault_pz_open_next'); } catch (e) {}
  }
  function uuid() { return (window.crypto && window.crypto.randomUUID) ? window.crypto.randomUUID() : 'v' + Date.now().toString(36) + Math.floor(performance.now()).toString(36); }
  function load() {
    if (mem) return mem;
    var now = Date.now();
    /* every stored value is checked; a value of the wrong shape is thrown away (cold start for that part) */
    var rep = lsGet(K.rep), dd = lsGet(K.dd), sess = lsGet(K.sess), cleanRep = {}, cleanDd = {};
    if (isPlain(rep)) Object.keys(rep).forEach(function (k) { var r = rep[k]; if (isPlain(r) && typeof r.s === 'string' && isFinite(r.t) && typeof r.t === 'number') cleanRep[k] = r; });
    if (isPlain(dd)) Object.keys(dd).forEach(function (k) { if (dd[k] === 1) cleanDd[k] = 1; });
    if (!(isPlain(sess) && typeof sess.id === 'string' && typeof sess.last === 'number' && isFinite(sess.last))) sess = null;
    mem = {
      events: R.pruneEvents(lsGet(K.ev), now),
      seen: R.pruneSeen(lsGet(K.seen), now),
      repeat: cleanRep,
      dedupe: cleanDd,
      sess: sess
    };
    return mem;
  }
  function session() {
    var m = load(), now = Date.now();
    if (!m.sess || now - m.sess.last > C.SESSION_TIMEOUT_MINUTES * 60000) { m.sess = { id: uuid(), last: now }; m.dedupe = {}; }
    m.sess.last = now;
    if (!lsGet(K.vid)) lsSet(K.vid, uuid()); // the one canonical personalization visitor ID
    return m.sess.id;
  }
  function save() {
    var m = mem, now = Date.now();
    if (!m || !allowed()) return;
    m.events = R.pruneEvents(m.events, now);
    m.seen = R.pruneSeen(m.seen, now);
    var cutoff = now - C.AFFINITY_MAX_AGE_DAYS * 86400000;
    var keys = Object.keys(m.repeat).filter(function (k) { return m.repeat[k].t >= cutoff; })
      .sort(function (a, b) { return m.repeat[b].t - m.repeat[a].t; }).slice(0, C.MAX_EVENTS);
    var rep = {}; keys.forEach(function (k) { rep[k] = m.repeat[k]; }); m.repeat = rep;
    lsSet(K.ev, m.events); lsSet(K.seen, m.seen); lsSet(K.rep, m.repeat); lsSet(K.dd, m.dedupe); lsSet(K.sess, m.sess);
  }
  function track(type, ctx, dedupeKey) {
    if (!allowed()) return false;
    var s = session(), m = load();
    if (dedupeKey) { if (m.dedupe[dedupeKey]) return false; m.dedupe[dedupeKey] = 1; }
    var credits = R.creditsFor(type, ctx);
    if (credits.length) m.events.push({ t: Date.now(), ty: type, s: s, c: credits });
    save();
    return !!credits.length;
  }
  /* product click: product credit once per product/session; artist credit once per (artist, product, session) */
  function trackProductClick(ctx) {
    if (!allowed() || !ctx || !ctx.product) return false;
    var s = session(), m = load();
    var pk = 'pc:' + ctx.product.id, ak = 'pca:' + (ctx.artist || '') + ':' + ctx.product.id;
    var artistish = {};
    if (ctx.artist) { artistish[ctx.artist] = 1; (ctx.groups || []).forEach(function (g) { artistish[g] = 1; }); }
    var credits = R.creditsFor('product_click_from_look', ctx).filter(function (cr) { return artistish[cr[0]] ? !m.dedupe[ak] : !m.dedupe[pk]; });
    m.dedupe[pk] = 1; if (ctx.artist) m.dedupe[ak] = 1;
    if (credits.length) m.events.push({ t: Date.now(), ty: 'product_click_from_look', s: s, c: credits });
    save();
    return !!credits.length;
  }
  /* qualified page view: 2s of visible time, once per page per session; repeated_product_view = return visit */
  function pageView(type, ctx, pageKey) {
    var done = false, timer = null, visibleFor = 0, lastStart = 0;
    function start() { if (done || timer || document.visibilityState !== 'visible') return; lastStart = Date.now(); timer = setTimeout(fire, Math.max(0, C.QUALIFIED_PAGE_VIEW_MS - visibleFor)); }
    function stop() { if (timer) { clearTimeout(timer); timer = null; visibleFor += Date.now() - lastStart; } }
    function fire() { try { fireNow(); } catch (err) { fail('pageView', err); mem = null; } }
    function fireNow() {
      done = true; timer = null;
      if (!allowed()) return;
      var s = session(), m = load();
      var first = track(type, ctx, 'pv:' + pageKey);
      if (first && type === 'product_page_view' && ctx.product) {
        var r = m.repeat[ctx.product.id];
        if (r && r.s !== s) track('repeated_product_view', ctx, 'rpv:' + ctx.product.id);
        m.repeat[ctx.product.id] = { s: s, t: Date.now() };
        save();
      }
    }
    document.addEventListener('visibilitychange', function () { if (document.visibilityState === 'visible') start(); else stop(); });
    start();
  }
  function seen(lookId) { if (!allowed() || !lookId) return; load().seen[lookId] = Date.now(); save(); }
  function debugOn() {
    if (/(?:\?|&)(?:vault_rank_debug|vault-pz-debug)=1/.test(window.location.search)) return true;
    try { if (window.localStorage.getItem('vault_rank_debug') === '1') return true; } catch (e) {}
    return !!(window.Shopify && window.Shopify.designMode);
  }
  function fail(where, err) { if (debugOn() && window.console) console.warn('[VaultRank] ' + where + ' failed, editorial order kept:', err); }
  /* Fail open: anything unexpected → the looks exactly as given (the editorial order) */
  function rankFor(input) {
    var res;
    try {
      input.consent = allowed();
      if (input.consent) {
        var m = load();
        input.events = m.events; input.seen = m.seen;
        input.sessionId = m.sess && (Date.now() - m.sess.last <= C.SESSION_TIMEOUT_MINUTES * 60000) ? m.sess.id : null;
      }
      input.now = Date.now();
      if (/(?:\?|&)vault-pz-force=1/.test(window.location.search)) input.forceConfidence = 1;
      res = R.rank(input);
    } catch (err) {
      fail('ranking', err);
      mem = null;
      return ((input && input.looks) || []).map(function (l) { return l.id; });
    }
    if (debugOn()) { try { report(input, res); } catch (e) {} }
    return res.order;
  }

  /* ---------- debug report: ?vault-pz-debug=1 ---------- */
  function report(input, res) {
    var d = res.debug, prof = d.profile || { long: {}, session: {} }, label = {};
    (input.looks || []).forEach(function (l) { if (l.artist && l.label) label[l.artist] = l.label; });
    function pick(map, prefix) {
      var o = {};
      Object.keys(map).filter(function (k) { return k.indexOf(prefix) === 0; }).sort(function (a, b) { return map[b] - map[a]; })
        .forEach(function (k) { o[(label[k] ? label[k] + ' ' : '') + k] = +map[k].toFixed(3); });
      return o;
    }
    var m = mem || {}, vid = null;
    try { vid = JSON.parse(window.localStorage.getItem(K.vid)); } catch (e) {}
    var out = {
      page: d.page, url: window.location.pathname, visitorId: vid, sessionId: d.sessionId || null,
      storage: allowed(), forcedConfidence: typeof input.forceConfidence === 'number',
      historyEventCount: d.eventCount || 0,
      recentEvents: (m.events || []).slice(-12).map(function (e) { return { type: e.ty, at: new Date(e.t).toISOString(), credits: e.c.map(function (c) { return c[0] + ' ' + (+c[1].toFixed(3)); }).join(', ') }; }),
      artistAffinity: pick(prof.long, 'artist:'), groupAffinity: pick(prof.long, 'group:'),
      productAffinity: pick(prof.long, 'product:'), brandAffinity: pick(prof.long, 'brand:'), categoryAffinity: pick(prof.long, 'cat:'),
      sessionIntent: Object.assign(pick(prof.session, 'artist:'), pick(prof.session, 'group:')),
      confidence: +d.confidence.toFixed(3),
      candidates: d.rows, diversity: d.diversity,
      finalOrder: d.rows.map(function (r, i) { return (i + 1) + '. ' + (r.label || r.artistId) + '  ' + r.lookId; })
    };
    window.__vaultPzDebug = out;
    if (!window.console) return;
    console.log('%cVAULT PERSONALIZATION DEBUG — ' + d.page + ' page', 'font-weight:bold');
    console.log('Visitor ID: ' + vid + '\nSession ID: ' + out.sessionId + '\nHistory event count: ' + out.historyEventCount + '\nPage confidence: ' + out.confidence + (out.forcedConfidence ? ' (FORCED)' : ''));
    if (console.table) { console.table(out.recentEvents); console.log('Artist affinity', out.artistAffinity, '\nGroup affinity', out.groupAffinity, '\nProduct affinity', out.productAffinity, '\nSession intent', out.sessionIntent); console.table(out.candidates); }
    console.log('FINAL SORTED ARRAY:\n' + out.finalOrder.join('\n'));
    d.diversity.forEach(function (x) { console.log('[diversity] ' + x); });
  }
  /* Sections call this after building, so the report shows what actually rendered */
  function rendered(what, list) {
    if (!debugOn()) return;
    var out = window.__vaultPzDebug = window.__vaultPzDebug || {};
    out[what] = list;
    var calc = (out.candidates || []).map(function (r) { return r.lookId; });
    if (what === 'domOrder') out.domMatchesFinal = JSON.stringify(list.map(function (x) { return x.lookId; })) === JSON.stringify(calc);
    if (window.console) console.log((what === 'domOrder' ? 'ACTUAL DOM ORDER' : 'ARTIST BUTTON ORDER') + ':\n' + list.map(function (x, i) { return (i + 1) + '. ' + (x.label || '') + (x.lookId ? '  ' + x.lookId : ''); }).join('\n') + (what === 'domOrder' ? '\nDOM matches final: ' + out.domMatchesFinal : ''));
  }

  /* ---------- successful add to cart ---------- */
  var productMeta = {};
  function rememberProduct(p) { if (p && p.id) productMeta[p.id] = p; }
  function onCartAdded(productId) {
    if (!productId) return;
    var id = 'product:' + productId;
    track('add_to_cart', { product: productMeta[id] || { id: id, brands: [], cats: [] } }, 'atc:' + id);
  }
  document.addEventListener('vault:cart-added', function (e) { onCartAdded((e.detail || {}).productId); });
  function hookPubSub() {
    if (typeof window.subscribe !== 'function' || !window.PUB_SUB_EVENTS || !window.PUB_SUB_EVENTS.cartUpdate) return false;
    window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, function (ev) {
      if (!ev || ev.source !== 'product-form' || !ev.cartData || ev.cartData.status) return; // only the theme form's success
      var d = ev.cartData, items = d.items ? d.items : [d];
      items.forEach(function (it) { if (it && it.product_id) onCartAdded(it.product_id); });
    });
    return true;
  }
  if (!hookPubSub()) document.addEventListener('DOMContentLoaded', hookPubSub, { once: true });

  /* Start on every page: load the stored history once so the visitor/session exist from the first page */
  try { if (allowed()) { load(); session(); save(); } } catch (e) { mem = null; }

  /* ---------- page readers ---------- */
  function catId(v) { return String(v || '').replace(/^gid:\/\/shopify\/TaxonomyCategory\//, '').trim(); }
  function productFromData(pid, brand, cat, anc) {
    if (!pid) return null;
    var p = { id: 'product:' + pid, brands: [], cats: [] };
    brand = String(brand || '').trim().toUpperCase();
    if (brand) p.brands.push('brand:' + brand);
    var leaf = catId(cat);
    if (leaf && leaf !== 'na') {
      /* Parent only when verified: taxonomy ids nest (aa-8-3 → aa-8) AND the page lists it as an ancestor.
         Otherwise the leaf alone gets the whole category credit. */
      var parent = leaf.indexOf('-') > -1 ? leaf.slice(0, leaf.lastIndexOf('-')) : '';
      var ancestors = String(anc || '').split(',').map(catId);
      if (parent && ancestors.indexOf(parent) > -1) {
        p.cats.push({ id: 'cat:' + leaf, w: C.PROPAGATION.categoryLeafShare });
        p.cats.push({ id: 'cat:' + parent, w: C.PROPAGATION.categoryParentShare });
      } else p.cats.push({ id: 'cat:' + leaf, w: 1 });
    }
    rememberProduct(p);
    return p;
  }
  function productFromSpot(sp) {
    if (!sp) return null;
    return productFromData(sp.getAttribute('data-product-id'), sp.getAttribute('data-brand'), sp.getAttribute('data-cat'), sp.getAttribute('data-cat-anc'));
  }
  function lookFromEl(el, pos, artist, groups) {
    var products = [], has = {};
    el.querySelectorAll('[data-vil-spot]').forEach(function (sp) {
      var p = productFromSpot(sp);
      if (p && !has[p.id]) { has[p.id] = 1; products.push(p); }
    });
    var gAttr = el.getAttribute('data-group-ids');
    return {
      id: el.getAttribute('data-look-id') || ('look:?:' + pos), el: el,
      artist: el.getAttribute('data-artist-id') || artist || '',
      groups: gAttr != null ? gAttr.split(',').filter(Boolean) : (groups || []),
      products: products, pos: pos,
      pinned: el.getAttribute('data-pin') === 'true',
      priority: Math.max(0, Math.min(10, parseInt(el.getAttribute('data-priority'), 10) || 0)),
      added: el.getAttribute('data-added') || '',
      hidden: !!el.querySelector('.vil-media--empty'),
      curatedPin: typeof el.__vilCurated === 'number' ? el.__vilCurated : -1
    };
  }
  var impressed = {};
  function watchImpression(el, lookId) {
    if (!('IntersectionObserver' in window) || !lookId || impressed[lookId]) return;
    var t = null;
    var io = new IntersectionObserver(function (en) {
      en.forEach(function (x) {
        if (x.intersectionRatio >= 0.5) { if (!t) t = setTimeout(function () { impressed[lookId] = 1; seen(lookId); io.disconnect(); }, C.QUALIFIED_IMPRESSION_MS); }
        else if (t) { clearTimeout(t); t = null; }
      });
    }, { threshold: [0, 0.5, 1] });
    io.observe(el);
  }

  function guard(name, fn) { return function () { try { return fn.apply(this, arguments); } catch (err) { fail(name, err); mem = null; return false; } }; }
  track = guard('track', track); trackProductClick = guard('trackProductClick', trackProductClick);
  seen = guard('seen', seen); pageView = guard('pageView', pageView);
  productFromData = guard('productFromData', productFromData);
  window.VaultPZ = {
    allowed: allowed, track: track, trackProductClick: trackProductClick, pageView: pageView, seen: seen,
    rank: rankFor, rendered: rendered, debugOn: debugOn, rememberProduct: rememberProduct, wipe: wipe,
    productFromData: productFromData, productFromSpot: productFromSpot, lookFromEl: lookFromEl, watchImpression: watchImpression
  };
})();
